import dayjs from "dayjs";

/** Check if the app is running in "standalone" mode, helpful for detecting PWA */
export function isStandalone(): boolean {
	return "standalone" in window.navigator && !!window.navigator.standalone;
}

/** Open a url externally:
 * If in a browser, will open in a new tab.
 * If in a PWA, will open in a new window instead of using the in-app browser.
 */
export function openExternalUrl(url: string) {
	const mode = isStandalone() ? "_top" : "_blank";
	const win = window.open(url, mode);
	win?.focus();
}

interface BeforeInstallPromptEvent extends Event {
	readonly platforms: string[];
	readonly userChoice: Promise<{
		outcome: "accepted" | "dismissed";
		platform: string;
	}>;
	prompt(): Promise<void>;
}
declare global {
	interface Window {
		deferredPrompt: BeforeInstallPromptEvent | null;
	}
}
export function captureInstallPrompt() {
	window.deferredPrompt = null;

	window.addEventListener("beforeinstallprompt", (e: Event) => {
		// Prevent the default browser install prompt
		e.preventDefault();

		// Store the event for later use
		window.deferredPrompt = e as BeforeInstallPromptEvent;
	});

	window.addEventListener("appinstalled", () => {
		window.deferredPrompt = null;
	});
}

interface InstallPromptState {
	dismissCount: number;
	lastDismissed?: number;
}
function getInstallPromptState(): InstallPromptState {
	const stored = localStorage.getItem("installPromptState");

	if (stored) {
		return JSON.parse(stored);
	}

	return {
		dismissCount: 0,
		lastDismissed: undefined,
	};
}
export function isInstallPromptTimedOut(): boolean {
	const state = getInstallPromptState();

	if (state.lastDismissed && state.dismissCount > 0) {
		const lastDismissedDate = dayjs(Number(state.lastDismissed));

		let timeoutExpireDate: dayjs.Dayjs;

		switch (state.dismissCount) {
			case 1:
				timeoutExpireDate = lastDismissedDate.add(1, "hour");
				break;
			case 2:
				timeoutExpireDate = lastDismissedDate.add(1, "day");
				break;
			case 3:
				timeoutExpireDate = lastDismissedDate.add(1, "week");
				break;
			case 4:
				timeoutExpireDate = lastDismissedDate.add(1, "month");
				break;
			default:
				timeoutExpireDate = lastDismissedDate.add(6, "month");
				break;
		}

		return dayjs().isBefore(timeoutExpireDate);
	}

	return false;
}

/** "Dismiss" the install prompt by timing it out for an exponential amount of time */
export function timeoutInstallPrompt() {
	const state = getInstallPromptState();
	state.dismissCount += 1;
	state.lastDismissed = Date.now();
	localStorage.setItem("installPromptState", JSON.stringify(state));
}
